import type { BriefSummary } from "../types";

interface Props {
  briefs: BriefSummary[];
  selectedId: string | null;
  loading: boolean;
  error: string | null;
  onSelect: (brief: BriefSummary) => void;
  onRefresh: () => void;
}

export function BriefList({ briefs, selectedId, loading, error, onSelect, onRefresh }: Props) {
  return (
    <aside className="panel brief-list">
      <div className="panel-header">
        <h2>Briefs</h2>
        <span className="info-dot" tabIndex={0} aria-label="About the briefs list">
          i
        </span>
        <button className="icon-btn" onClick={onRefresh} title="Refresh briefs" disabled={loading}>
          ⟳
        </button>
        <div className="info-tip" role="tooltip">
          <p>
            Shows North files named <code>brief-YYYY-MM-DD.md</code>, optionally with a{" "}
            <code>-label</code> suffix (<code>brief-YYYY-MM-DD-morning.md</code>) so a day can hold several.
          </p>
          <p>
            <code>brief-instructions.md</code> and any other files are excluded. Generation saves the day's
            first run as <code>brief-&lt;date&gt;.md</code> and later runs as{" "}
            <code>brief-&lt;date&gt;-&lt;time&gt;.md</code>.
          </p>
        </div>
      </div>
      <div className="panel-body scroll">
        {error && <div className="notice error">{error}</div>}
        {!error && briefs.length === 0 && !loading && (
          <div className="notice muted">No briefs yet. Use “Generate now” in Settings.</div>
        )}
        <ul className="brief-items">
          {briefs.map((b) => (
            <li key={b.id}>
              <button
                className={`brief-item ${b.id === selectedId ? "active" : ""}`}
                onClick={() => onSelect(b)}
              >
                <span className="brief-date">
                  {b.date}
                  {b.label && <span className="brief-label">{b.label}</span>}
                </span>
                <span className="brief-meta">{formatBytes(b.bytes)}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
